import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocalStorage } from "rujira.ui";
import { OrderType } from "../../types";
import { useTradeUrlState } from "../../tradeUrlState";

export enum Tab {
  Limit = "limit",
  Tracking = "tracking",
  Recurring = "recurring",
  Automated = "automated",
}

const context = createContext<{
  only: boolean;
  setOnly: (v: boolean) => void;
  tab: Tab;
  selectTab: (v: Tab) => void;
  selectPreviousTab: () => void;
}>({
  only: false,
  setOnly: () => {},
  tab: Tab.Limit,
  selectTab: () => {},
  selectPreviousTab: () => {},
});

export const Context: FC<PropsWithChildren> = ({ children }) => {
  const [only, setOnly] = useLocalStorage("trade-only", false);
  const [tradeUrlState, setTradeUrlState] = useTradeUrlState();
  const [tab, setTab] = useState(() =>
    tradeUrlState.type === "automated" ? Tab.Automated : Tab.Limit
  );
  const previousMode = useRef(tradeUrlState.type);
  const previousManualTab = useRef<Tab>(Tab.Limit);

  useEffect(() => {
    if (tradeUrlState.type === previousMode.current) return;

    previousMode.current = tradeUrlState.type;
    // The overview follows mode changes, but manual order-form changes do not
    // replace a tab the user explicitly selected in the overview.
    setTab(
      tradeUrlState.type === "automated"
        ? Tab.Automated
        : previousManualTab.current
    );
  }, [tradeUrlState.type]);

  const selectTab = (nextTab: Tab) => {
    if (nextTab !== Tab.Automated) previousManualTab.current = nextTab;
    setTab(nextTab);
  };

  const selectPreviousTab = () => {
    const previousTab = previousManualTab.current;
    setTradeUrlState({
      type: "manual",
      order:
        previousTab === Tab.Recurring ? OrderType.Recurring : OrderType.Limit,
    });
  };

  return (
    <context.Provider
      value={{
        only,
        setOnly,
        tab,
        selectTab,
        selectPreviousTab,
      }}>
      {children}
    </context.Provider>
  );
};

export const useOrdersContext = () => useContext(context);

export const TabsOverflowContext = createContext(false);
